import { Component, EventEmitter, Input, Output } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

import { ConnectionStatus, Provider } from '../../../api-clients/oem-connection';
import { DialogService } from '../../../services/dialog.service';
import { OemIntegrationService } from '../../../services/oem-integration.service';
import { formatLocalTime } from '../../../utils/format-local-time';

@Component({
    selector: 'app-manage-view',
    templateUrl: './manage-view.component.html',
    styleUrls: ['./manage-view.component.css'],
    standalone: false
})
export class ManageViewComponent {
  @Input() provider!: Provider;
  @Input() providerLabel = '';
  @Input() status!: ConnectionStatus;
  @Input() uiStatus = '';
  @Input() statusChip = '';

  @Output() closed = new EventEmitter<void>();

  busy = false;

  // Expose Provider for the template's *ngIf gating of the per-org grants panel.
  readonly Provider = Provider;

  constructor(
    private oemSvc: OemIntegrationService,
    private dialog: DialogService,
    private toastr: ToastrService,
  ) {}

  get isDeere(): boolean {
    return this.provider === Provider.Deere;
  }

  /**
   * SRA003-430: the vendor refresh grant is dead — BE flipped the connection
   * to 'expired' and /connections passes it through. Terminal state: surface
   * a non-green banner + reconnect CTA at the top of the manage view.
   * Distinct from 'revoked' (user-intent), which is left as-is.
   */
  get isExpired(): boolean {
    return this.status?.status === 'expired';
  }

  /**
   * Reconnect an expired connection — straight into the vendor OAuth flow
   * (no confirm dialog; the connection is already dead, so there is nothing
   * to protect). The BE upserts on UNIQUE(contractor, provider). Provider-
   * agnostic: Deere + CNH both reach 'expired'.
   */
  reconnect(): void {
    if (this.busy) return;
    this.busy = true;
    this.oemSvc.startConnect(this.provider).subscribe({
      error: () => {
        this.busy = false;
        this.toastr.error(
          `Could not start the reconnect flow for ${this.providerLabel}.`,
        );
      },
    });
  }

  formatTime(iso: string | null | undefined): string {
    return formatLocalTime(iso);
  }

  /** Re-link triggers a fresh OAuth flow; BE upserts via UNIQUE(contractor, provider). */
  reLink(): void {
    this.dialog
      .confirmDialog({
        title: 'Re-link account',
        message: `This will start a new OAuth flow for ${this.providerLabel} and replace the existing connection. Continue?`,
        confirmCaption: 'Re-link',
        cancelCaption: 'Cancel',
      })
      .subscribe((yes) => {
        if (!yes) return;
        this.busy = true;
        this.oemSvc.startConnect(this.provider).subscribe({
          error: () => {
            this.busy = false;
            this.toastr.error(
              `Could not start re-link flow for ${this.providerLabel}`,
            );
          },
        });
      });
  }

  disconnect(): void {
    this.dialog
      .confirmDialog({
        title: 'Disconnect',
        message: `Disconnect ${this.providerLabel}? Telemetry will stop flowing into Harvest Mate. You can reconnect later.`,
        confirmCaption: 'Disconnect',
        cancelCaption: 'Cancel',
      })
      .subscribe((yes) => {
        if (!yes) return;
        this.busy = true;
        this.oemSvc.disconnect(this.provider).subscribe({
          next: () => {
            this.busy = false;
            this.toastr.success(`Disconnected from ${this.providerLabel}`);
            this.closed.emit();
          },
          error: () => {
            this.busy = false;
            this.toastr.error(
              `Could not disconnect from ${this.providerLabel}`,
            );
          },
        });
      });
  }

  back(): void {
    this.closed.emit();
  }
}
